import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectRouteSegments,
    updateRouteSegment,
    setCurrentlyEditingSegmentIndex,
    selectCurrentlyEditingSegmentIndex,
    checkSegmentsStepCompleted,
} from '../../../features/tripcenter/googleRouteMakerSlice';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';

// Icons
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';

const SegmentDefinition = () => {
    const dispatch = useDispatch();
    const routeSegments = useSelector(selectRouteSegments);
    const currentlyEditingSegmentIndex = useSelector(selectCurrentlyEditingSegmentIndex);

    // Local state for editing segment
    const [editFormData, setEditFormData] = useState({
        name: '',
        loadType: 'loaded'
    });

    // Start editing a segment
    const handleEditSegment = (index) => {
        dispatch(setCurrentlyEditingSegmentIndex(index));
        setEditFormData({
            name: routeSegments[index].name,
            loadType: routeSegments[index].loadType
        });
    };

    // Save segment changes
    const handleSaveSegment = () => {
        const index = currentlyEditingSegmentIndex;
        if (index === null) return;

        const updatedSegment = {
            ...routeSegments[index],
            name: editFormData.name,
            loadType: editFormData.loadType
        };

        dispatch(updateRouteSegment({ index, segment: updatedSegment }));
        dispatch(setCurrentlyEditingSegmentIndex(null));
        dispatch(checkSegmentsStepCompleted());
    };

    // Update form data
    const handleChange = (e) => {
        setEditFormData({
            ...editFormData,
            [e.target.name]: e.target.value
        });
    };

    // Cancel editing
    const handleCancelEdit = () => {
        dispatch(setCurrentlyEditingSegmentIndex(null));
    };

    // Check completion status when segments change
    useEffect(() => {
        dispatch(checkSegmentsStepCompleted());
    }, [routeSegments, dispatch]);

    // If no segments exist yet
    if (!routeSegments || routeSegments.length === 0) {
        return (
            <Box p={3}>
                <Alert severity="warning">
                    No route segments have been generated. Please complete the route building step first.
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                Define Route Segments
            </Typography>

            <Typography variant="body1" color="textSecondary" paragraph>
                A route segment is a section of your route between two consecutive locations.
                Define names and load types for each segment.
            </Typography>

            <Box sx={{ mt: 2, mb: 3 }}>
                <Paper variant="outlined">
                    <Box p={2} display="flex" alignItems="center">
                        <InfoIcon color="info" sx={{ mr: 2 }} />
                        <Typography variant="body2">
                            <strong>Load Type:</strong> "Loaded" means the vehicle is carrying cargo, while
                            "Empty" means it's traveling without cargo.
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell width="5%">#</TableCell>
                            {/* <TableCell width="35%">From → To</TableCell> */}
                            <TableCell width="25%">Name</TableCell>
                            <TableCell width="15%">Distance</TableCell>
                            <TableCell width="15%">Load Type</TableCell>
                            <TableCell width="5%">Edit</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {routeSegments.map((segment, index) => (
                            <TableRow key={index} sx={{
                                bgcolor: currentlyEditingSegmentIndex === index ? 'action.selected' : 'inherit'
                            }}>
                                <TableCell>{index + 1}</TableCell>
                                {/* <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                        {segment.from?.name || 'Start'} → {segment.to?.name || 'End'}
                                    </Typography>
                                </TableCell> */}

                                {currentlyEditingSegmentIndex === index ? (
                                    // Edit mode cells
                                    <>
                                        <TableCell>
                                            <TextField
                                                name="name"
                                                value={editFormData.name}
                                                onChange={handleChange}
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                placeholder="Segment name"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {(segment.distance / 1000).toFixed(1)} km
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    name="loadType"
                                                    value={editFormData.loadType}
                                                    onChange={handleChange}
                                                >
                                                    <MenuItem value="loaded">Loaded</MenuItem>
                                                    <MenuItem value="empty">Empty</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex">
                                                <IconButton size="small" onClick={handleSaveSegment} color="primary">
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={handleCancelEdit} color="inherit">
                                                    <CancelIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </>
                                ) : (
                                    // View mode cells
                                    <>
                                        <TableCell>{segment.name}</TableCell>
                                        <TableCell>{(segment.distance / 1000).toFixed(1)} km</TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<LocalShippingIcon />}
                                                label={segment.loadType === 'loaded' ? 'Loaded' : 'Empty'}
                                                size="small"
                                                color={segment.loadType === 'loaded' ? 'success' : 'default'}
                                                variant={segment.loadType === 'loaded' ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => handleEditSegment(index)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box mt={4} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                    All segments must have a name and load type before proceeding.
                </Typography>
            </Box>
        </Box>
    );
};

export default SegmentDefinition; 